import { api } from './emptyApi'

export const addTagTypes = ['workflows', 'workflow-versions', 'workflow-runs', 'in-app-notifications'] as const
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes
  })
  .injectEndpoints({
    endpoints: (build) => ({
      validateWorkflowDefinition: build.mutation<ValidateWorkflowDefinitionResponse, ValidateWorkflowDefinitionArgs>({
        query: (queryArg) => ({
          url: `/api/v1/workflows/validate`,
          method: 'POST',
          body: queryArg.validateDefinitionRequest
        }),
        invalidatesTags: ['workflows']
      }),
      listWorkflows: build.query<ListWorkflowsResponse, ListWorkflowsArgs>({
        query: (queryArg) => ({
          url: `/api/v1/workflows`,
          params: {
            nameContains: queryArg.nameContains,
            enabled: queryArg.enabled,
            live: queryArg.live,
            includeArchived: queryArg.includeArchived,
            page: queryArg.page,
            count: queryArg.count
          }
        }),
        providesTags: ['workflows']
      }),
      createWorkflow: build.mutation<CreateWorkflowResponse, CreateWorkflowArgs>({
        query: (queryArg) => ({ url: `/api/v1/workflows`, method: 'POST', body: queryArg.createWorkflowRequest }),
        invalidatesTags: ['workflows']
      }),
      getWorkflow: build.query<GetWorkflowResponse, GetWorkflowArgs>({
        query: (queryArg) => ({ url: `/api/v1/workflows/${queryArg.workflowId}` }),
        providesTags: ['workflows']
      }),
      patchWorkflow: build.mutation<PatchWorkflowResponse, PatchWorkflowArgs>({
        query: (queryArg) => ({
          url: `/api/v1/workflows/${queryArg.workflowId}`,
          method: 'PATCH',
          body: queryArg.patchWorkflowRequest
        }),
        invalidatesTags: ['workflows']
      }),
      archiveWorkflow: build.mutation<ArchiveWorkflowResponse, ArchiveWorkflowArgs>({
        query: (queryArg) => ({ url: `/api/v1/workflows/${queryArg.workflowId}`, method: 'DELETE' }),
        invalidatesTags: ['workflows']
      }),
      listVersions: build.query<ListVersionsResponse, ListVersionsArgs>({
        query: (queryArg) => ({
          url: `/api/v1/workflows/${queryArg.workflowId}/versions`,
          params: {
            page: queryArg.page,
            count: queryArg.count
          }
        }),
        providesTags: ['workflow-versions']
      }),
      publishVersion: build.mutation<PublishVersionResponse, PublishVersionArgs>({
        query: (queryArg) => ({
          url: `/api/v1/workflows/${queryArg.workflowId}/versions`,
          method: 'POST',
          body: queryArg.publishVersionRequest
        }),
        invalidatesTags: ['workflow-versions']
      }),
      getVersion: build.query<GetVersionResponse, GetVersionArgs>({
        query: (queryArg) => ({ url: `/api/v1/workflows/${queryArg.workflowId}/versions/${queryArg.versionNumber}` }),
        providesTags: ['workflow-versions']
      }),
      listRuns: build.query<ListRunsResponse, ListRunsArgs>({
        query: (queryArg) => ({
          url: `/api/v1/workflow-runs`,
          params: {
            workflowId: queryArg.workflowId,
            status: queryArg.status,
            dryRun: queryArg.dryRun,
            version: queryArg.version,
            createdAfter: queryArg.createdAfter,
            createdBefore: queryArg.createdBefore,
            page: queryArg.page,
            count: queryArg.count
          }
        }),
        providesTags: ['workflow-runs']
      }),
      getRun: build.query<GetRunResponse, GetRunArgs>({
        query: (queryArg) => ({ url: `/api/v1/workflow-runs/${queryArg.runId}` }),
        providesTags: ['workflow-runs']
      }),
      getRunLogs: build.query<GetRunLogsResponse, GetRunLogsArgs>({
        query: (queryArg) => ({ url: `/api/v1/workflow-runs/${queryArg.runId}/logs` }),
        providesTags: ['workflow-runs']
      }),
      cancelRun: build.mutation<CancelRunResponse, CancelRunArgs>({
        query: (queryArg) => ({ url: `/api/v1/workflow-runs/${queryArg.runId}/cancel`, method: 'POST' }),
        invalidatesTags: ['workflow-runs']
      }),
      rerunRun: build.mutation<RerunRunResponse, RerunRunArgs>({
        query: (queryArg) => ({
          url: `/api/v1/workflow-runs/${queryArg.runId}/rerun`,
          method: 'POST',
          params: {
            fromFailure: queryArg.fromFailure
          }
        }),
        invalidatesTags: ['workflow-runs']
      }),
      createInAppNotifications: build.mutation<CreateInAppNotificationsResponse, CreateInAppNotificationsArgs>({
        query: (queryArg) => ({
          url: `/api/v1/in-app-notifications`,
          method: 'POST',
          body: queryArg.createInAppNotificationsRequest
        }),
        invalidatesTags: ['in-app-notifications']
      }),
      listInAppNotifications: build.query<ListInAppNotificationsResponse, ListInAppNotificationsArgs>({
        query: (queryArg) => ({
          url: `/api/v1/in-app-notifications`,
          params: {
            read: queryArg.read,
            priority: queryArg.priority,
            page: queryArg.page,
            count: queryArg.count
          }
        }),
        providesTags: ['in-app-notifications']
      }),
      markAllInAppNotificationsRead: build.mutation<
        MarkAllInAppNotificationsReadResponse,
        MarkAllInAppNotificationsReadArgs
      >({
        query: () => ({ url: `/api/v1/in-app-notifications/mark-all-read`, method: 'POST' }),
        invalidatesTags: ['in-app-notifications']
      }),
      markInAppNotificationsReadByIds: build.mutation<
        MarkInAppNotificationsReadByIdsResponse,
        MarkInAppNotificationsReadByIdsArgs
      >({
        query: (queryArg) => ({
          url: `/api/v1/in-app-notifications/mark-read`,
          method: 'POST',
          body: queryArg.markReadByIdsRequest
        }),
        invalidatesTags: ['in-app-notifications']
      }),
      markInAppNotificationRead: build.mutation<MarkInAppNotificationReadResponse, MarkInAppNotificationReadArgs>({
        query: (queryArg) => ({
          url: `/api/v1/in-app-notifications/${queryArg.notificationId}`,
          method: 'PATCH',
          body: queryArg.markReadRequest
        }),
        invalidatesTags: ['in-app-notifications']
      })
    }),
    overrideExisting: false
  })
export { injectedRtkApi as enhancedApi }
export type ValidateWorkflowDefinitionResponse = /** status 200 Successful Response */ ValidateDefinitionResponseRead
export type ValidateWorkflowDefinitionArgs = {
  validateDefinitionRequest: ValidateDefinitionRequest
}
export type ListWorkflowsResponse = /** status 200 Successful Response */ PagedResponseWorkflowResponse
export type ListWorkflowsArgs = {
  /** Case-insensitive substring match on name */
  nameContains?: string
  /** Filter by the user switch */
  enabled?: boolean
  /** Filter on liveness: enabled AND an active version is set */
  live?: boolean
  /** Include soft-archived workflows (excluded by default) */
  includeArchived?: boolean
  /** Page number (0-indexed) */
  page?: number
  /** Number of items per page */
  count?: number
}
export type CreateWorkflowResponse = /** status 201 Successful Response */ WorkflowResponse
export type CreateWorkflowArgs = {
  createWorkflowRequest: CreateWorkflowRequest
}
export type GetWorkflowResponse = /** status 200 Successful Response */ WorkflowResponse
export type GetWorkflowArgs = {
  workflowId: string
}
export type PatchWorkflowResponse = /** status 200 Successful Response */ WorkflowResponse
export type PatchWorkflowArgs = {
  workflowId: string
  patchWorkflowRequest: PatchWorkflowRequest
}
export type ArchiveWorkflowResponse = unknown
export type ArchiveWorkflowArgs = {
  workflowId: string
}
export type ListVersionsResponse = /** status 200 Successful Response */ PagedResponseWorkflowVersionSummary
export type ListVersionsArgs = {
  workflowId: string
  /** Page number (0-indexed) */
  page?: number
  /** Number of items per page */
  count?: number
}
export type PublishVersionResponse = /** status 201 Successful Response */ WorkflowVersionResponse
export type PublishVersionArgs = {
  workflowId: string
  publishVersionRequest: PublishVersionRequest
}
export type GetVersionResponse = /** status 200 Successful Response */ WorkflowVersionResponse
export type GetVersionArgs = {
  workflowId: string
  versionNumber: number
}
export type ListRunsResponse = /** status 200 Successful Response */ PagedResponseRunSummary
export type ListRunsArgs = {
  /** Only runs of this workflow */
  workflowId?: string
  /** Filter by run state */
  status?: RunState
  /** Filter by execution mode (true = dry-run only) */
  dryRun?: boolean
  /** Only runs of this workflow definition version */
  version?: number
  /** Only runs created at or after this instant (inclusive) */
  createdAfter?: string
  /** Only runs created at or before this instant (inclusive) */
  createdBefore?: string
  /** Page number (0-indexed) */
  page?: number
  /** Number of items per page */
  count?: number
}
export type GetRunResponse = /** status 200 Successful Response */ RunDetailResponse
export type GetRunArgs = {
  runId: string
}
export type GetRunLogsResponse = /** status 200 Successful Response */ RunLogsResponse
export type GetRunLogsArgs = {
  runId: string
}
export type CancelRunResponse = /** status 200 Successful Response */ CancelResponse
export type CancelRunArgs = {
  runId: string
}
export type RerunRunResponse = /** status 201 Successful Response */ RerunResponse
export type RerunRunArgs = {
  runId: string
  /** true resumes from the failed step (FAILED runs only); false re-runs from the root (any terminal run). */
  fromFailure: boolean
}
export type CreateInAppNotificationsResponse = /** status 201 Successful Response */ CreatedInAppNotifications
export type CreateInAppNotificationsArgs = {
  createInAppNotificationsRequest: CreateInAppNotificationsRequest
}
export type ListInAppNotificationsResponse =
  /** status 200 Successful Response */ PagedResponseInAppNotificationResponse
export type ListInAppNotificationsArgs = {
  /** Filter by read state */
  read?: boolean
  /** Filter by priority */
  priority?: NotificationPriority
  /** Page number (0-indexed) */
  page?: number
  /** Number of items per page */
  count?: number
}
export type MarkAllInAppNotificationsReadResponse = /** status 200 Successful Response */ MarkAllReadResult
export type MarkAllInAppNotificationsReadArgs = void
export type MarkInAppNotificationsReadByIdsResponse = /** status 200 Successful Response */ MarkReadByIdsResult
export type MarkInAppNotificationsReadByIdsArgs = {
  markReadByIdsRequest: MarkReadByIdsRequest
}
export type MarkInAppNotificationReadResponse = /** status 200 Successful Response */ InAppNotificationResponse
export type MarkInAppNotificationReadArgs = {
  notificationId: string
  markReadRequest: MarkReadRequest
}
export type TriggerCount = {
  loc?: (string | number)[]
  code: 'trigger_count'
}
export type TriggerCountRead = {
  loc?: (string | number)[]
  code: 'trigger_count'
  message: string
}
export type UnknownAction = {
  loc?: (string | number)[]
  code: 'unknown_action'
  action: string
}
export type UnknownActionRead = {
  loc?: (string | number)[]
  code: 'unknown_action'
  action: string
  message: string
}
export type UnknownParameter = {
  loc?: (string | number)[]
  code: 'unknown_parameter'
  parameter: string
  action: string
}
export type UnknownParameterRead = {
  loc?: (string | number)[]
  code: 'unknown_parameter'
  parameter: string
  action: string
  message: string
}
export type TemplatingNotSupported = {
  loc?: (string | number)[]
  code: 'templating_not_supported'
  parameter: string
}
export type TemplatingNotSupportedRead = {
  loc?: (string | number)[]
  code: 'templating_not_supported'
  parameter: string
  message: string
}
export type UnknownEventAttribute = {
  loc?: (string | number)[]
  code: 'unknown_event_attribute'
  attribute: string
  eventType: string
}
export type UnknownEventAttributeRead = {
  loc?: (string | number)[]
  code: 'unknown_event_attribute'
  attribute: string
  eventType: string
  message: string
}
export type OperatorNotValidForType = {
  loc?: (string | number)[]
  code: 'operator_not_valid_for_type'
  operator: string
  field: string
  fieldType: string
}
export type OperatorNotValidForTypeRead = {
  loc?: (string | number)[]
  code: 'operator_not_valid_for_type'
  operator: string
  field: string
  fieldType: string
  message: string
}
export type OneOfRequiresList = {
  loc?: (string | number)[]
  code: 'one_of_requires_list'
  field: string
}
export type OneOfRequiresListRead = {
  loc?: (string | number)[]
  code: 'one_of_requires_list'
  field: string
  message: string
}
export type BetweenRequiresPair = {
  loc?: (string | number)[]
  code: 'between_requires_pair'
  field: string
}
export type BetweenRequiresPairRead = {
  loc?: (string | number)[]
  code: 'between_requires_pair'
  field: string
  message: string
}
export type ConditionElementsWrongType = {
  loc?: (string | number)[]
  code: 'condition_elements_wrong_type'
  operator: string
  field: string
  fieldType: string
}
export type ConditionElementsWrongTypeRead = {
  loc?: (string | number)[]
  code: 'condition_elements_wrong_type'
  operator: string
  field: string
  fieldType: string
  message: string
}
export type ConditionValueWrongType = {
  loc?: (string | number)[]
  code: 'condition_value_wrong_type'
  field: string
  fieldType: string
}
export type ConditionValueWrongTypeRead = {
  loc?: (string | number)[]
  code: 'condition_value_wrong_type'
  field: string
  fieldType: string
  message: string
}
export type ConditionReferenceTypeMismatch = {
  loc?: (string | number)[]
  code: 'condition_reference_type_mismatch'
  field: string
  fieldType: string
  reference: string
  referenceType: string
}
export type ConditionReferenceTypeMismatchRead = {
  loc?: (string | number)[]
  code: 'condition_reference_type_mismatch'
  field: string
  fieldType: string
  reference: string
  referenceType: string
  message: string
}
export type ConditionReferenceNotValidForOperator = {
  loc?: (string | number)[]
  code: 'condition_reference_not_valid_for_operator'
  operator: string
  field: string
}
export type ConditionReferenceNotValidForOperatorRead = {
  loc?: (string | number)[]
  code: 'condition_reference_not_valid_for_operator'
  operator: string
  field: string
  message: string
}
export type ConditionStepNotAvailable = {
  loc?: (string | number)[]
  code: 'condition_step_not_available'
  reference: string
}
export type ConditionStepNotAvailableRead = {
  loc?: (string | number)[]
  code: 'condition_step_not_available'
  reference: string
  message: string
}
export type TemplateMalformed = {
  loc?: (string | number)[]
  code: 'template_malformed'
  reference: string
}
export type TemplateMalformedRead = {
  loc?: (string | number)[]
  code: 'template_malformed'
  reference: string
  message: string
}
export type TemplateEventShape = {
  loc?: (string | number)[]
  code: 'template_event_shape'
  reference: string
}
export type TemplateEventShapeRead = {
  loc?: (string | number)[]
  code: 'template_event_shape'
  reference: string
  message: string
}
export type TemplateUnknownRoot = {
  loc?: (string | number)[]
  code: 'template_unknown_root'
  reference: string
}
export type TemplateUnknownRootRead = {
  loc?: (string | number)[]
  code: 'template_unknown_root'
  reference: string
  message: string
}
export type TemplateUnknownStep = {
  loc?: (string | number)[]
  code: 'template_unknown_step'
  step: string
}
export type TemplateUnknownStepRead = {
  loc?: (string | number)[]
  code: 'template_unknown_step'
  step: string
  message: string
}
export type TemplateStepNotPredecessor = {
  loc?: (string | number)[]
  code: 'template_step_not_predecessor'
  step: string
  referencingStep: string
}
export type TemplateStepNotPredecessorRead = {
  loc?: (string | number)[]
  code: 'template_step_not_predecessor'
  step: string
  referencingStep: string
  message: string
}
export type TemplateStepShape = {
  loc?: (string | number)[]
  code: 'template_step_shape'
}
export type TemplateStepShapeRead = {
  loc?: (string | number)[]
  code: 'template_step_shape'
  message: string
}
export type TemplateTargetNotAction = {
  loc?: (string | number)[]
  code: 'template_target_not_action'
  step: string
}
export type TemplateTargetNotActionRead = {
  loc?: (string | number)[]
  code: 'template_target_not_action'
  step: string
  message: string
}
export type TemplateUnknownResultField = {
  loc?: (string | number)[]
  code: 'template_unknown_result_field'
  field: string
  step: string
}
export type TemplateUnknownResultFieldRead = {
  loc?: (string | number)[]
  code: 'template_unknown_result_field'
  field: string
  step: string
  message: string
}
export type ContextPathInvalid = {
  loc?: (string | number)[]
  code: 'context_path_invalid'
  reference: string
  eventType: string
}
export type ContextPathInvalidRead = {
  loc?: (string | number)[]
  code: 'context_path_invalid'
  reference: string
  eventType: string
  message: string
}
export type ContextUnknownEntity = {
  loc?: (string | number)[]
  code: 'context_unknown_entity'
  reference: string
  eventType: string
  entity: string
}
export type ContextUnknownEntityRead = {
  loc?: (string | number)[]
  code: 'context_unknown_entity'
  reference: string
  eventType: string
  entity: string
  message: string
}
export type ContextEntityUnreachable = {
  loc?: (string | number)[]
  code: 'context_entity_unreachable'
  reference: string
  eventType: string
  entity: string
  fromEntity?: string | null
}
export type ContextEntityUnreachableRead = {
  loc?: (string | number)[]
  code: 'context_entity_unreachable'
  reference: string
  eventType: string
  entity: string
  fromEntity?: string | null
  message: string
}
export type ContextEntityExcludedByTrigger = {
  loc?: (string | number)[]
  code: 'context_entity_excluded_by_trigger'
  reference: string
  eventType: string
  entity: string
  attribute: string
  value: string
  pinned: string[]
}
export type ContextEntityExcludedByTriggerRead = {
  loc?: (string | number)[]
  code: 'context_entity_excluded_by_trigger'
  reference: string
  eventType: string
  entity: string
  attribute: string
  value: string
  pinned: string[]
  message: string
}
export type ContextUnknownField = {
  loc?: (string | number)[]
  code: 'context_unknown_field'
  reference: string
  eventType: string
  entity: string
  field: string
}
export type ContextUnknownFieldRead = {
  loc?: (string | number)[]
  code: 'context_unknown_field'
  reference: string
  eventType: string
  entity: string
  field: string
  message: string
}
export type ContextNotAvailableInTrigger = {
  loc?: (string | number)[]
  code: 'context_not_available_in_trigger'
  reference: string
  eventType: string
}
export type ContextNotAvailableInTriggerRead = {
  loc?: (string | number)[]
  code: 'context_not_available_in_trigger'
  reference: string
  eventType: string
  message: string
}
export type LinkMalformed = {
  loc?: (string | number)[]
  code: 'link_malformed'
  token: string
  reason: string
}
export type LinkMalformedRead = {
  loc?: (string | number)[]
  code: 'link_malformed'
  token: string
  reason: string
  message: string
}
export type LinkEntityKind = 'charger' | 'vehicle' | 'site' | 'battery' | 'generator' | 'panel' | 'link'
export type LinkEntityUnknown = {
  loc?: (string | number)[]
  code: 'link_entity_unknown'
  entity: string
  supported: LinkEntityKind[]
}
export type LinkEntityUnknownRead = {
  loc?: (string | number)[]
  code: 'link_entity_unknown'
  entity: string
  supported: LinkEntityKind[]
  message: string
}
export type LinkNotAllowedHere = {
  loc?: (string | number)[]
  code: 'link_not_allowed_here'
  parameter: string
}
export type LinkNotAllowedHereRead = {
  loc?: (string | number)[]
  code: 'link_not_allowed_here'
  parameter: string
  message: string
}
export type LinkLabelEmpty = {
  loc?: (string | number)[]
  code: 'link_label_empty'
}
export type LinkLabelEmptyRead = {
  loc?: (string | number)[]
  code: 'link_label_empty'
  message: string
}
export type RootStepNotInSteps = {
  loc?: (string | number)[]
  code: 'root_step_not_in_steps'
  root: string
}
export type RootStepNotInStepsRead = {
  loc?: (string | number)[]
  code: 'root_step_not_in_steps'
  root: string
  message: string
}
export type StepKeyMismatch = {
  loc?: (string | number)[]
  code: 'step_key_mismatch'
  key: string
  id: string
}
export type StepKeyMismatchRead = {
  loc?: (string | number)[]
  code: 'step_key_mismatch'
  key: string
  id: string
  message: string
}
export type UnknownSuccessor = {
  loc?: (string | number)[]
  code: 'unknown_successor'
  step: string
}
export type UnknownSuccessorRead = {
  loc?: (string | number)[]
  code: 'unknown_successor'
  step: string
  message: string
}
export type StepCycle = {
  loc?: (string | number)[]
  code: 'step_cycle'
  step: string
  fromStep: string
}
export type StepCycleRead = {
  loc?: (string | number)[]
  code: 'step_cycle'
  step: string
  fromStep: string
  message: string
}
export type UnreachableSteps = {
  loc?: (string | number)[]
  code: 'unreachable_steps'
  steps: string
}
export type UnreachableStepsRead = {
  loc?: (string | number)[]
  code: 'unreachable_steps'
  steps: string
  message: string
}
export type BranchDefaultPosition = {
  loc?: (string | number)[]
  code: 'branch_default_position'
  step: string
}
export type BranchDefaultPositionRead = {
  loc?: (string | number)[]
  code: 'branch_default_position'
  step: string
  message: string
}
export type OperatorMustNotCarryValue = {
  loc?: (string | number)[]
  code: 'operator_must_not_carry_value'
  operator: string
}
export type OperatorMustNotCarryValueRead = {
  loc?: (string | number)[]
  code: 'operator_must_not_carry_value'
  operator: string
  message: string
}
export type OperatorRequiresValue = {
  loc?: (string | number)[]
  code: 'operator_requires_value'
  operator: string
}
export type OperatorRequiresValueRead = {
  loc?: (string | number)[]
  code: 'operator_requires_value'
  operator: string
  message: string
}
export type FlatSpecWithGroups = {
  loc?: (string | number)[]
  code: 'flat_spec_with_groups'
}
export type FlatSpecWithGroupsRead = {
  loc?: (string | number)[]
  code: 'flat_spec_with_groups'
  message: string
}
export type GroupedSpecWithFlatKeys = {
  loc?: (string | number)[]
  code: 'grouped_spec_with_flat_keys'
  keys: string[]
}
export type GroupedSpecWithFlatKeysRead = {
  loc?: (string | number)[]
  code: 'grouped_spec_with_flat_keys'
  keys: string[]
  message: string
}
export type WindowExceedsCap = {
  loc?: (string | number)[]
  code: 'window_exceeds_cap'
  window: string
  maxWindow: string
}
export type WindowExceedsCapRead = {
  loc?: (string | number)[]
  code: 'window_exceeds_cap'
  window: string
  maxWindow: string
  message: string
}
export type NotHeardFromWindowTooShort = {
  loc?: (string | number)[]
  code: 'not_heard_from_window_too_short'
  window: string
  minimum: string
}
export type NotHeardFromWindowTooShortRead = {
  loc?: (string | number)[]
  code: 'not_heard_from_window_too_short'
  window: string
  minimum: string
  message: string
}
export type UnsupportedEntityType = {
  loc?: (string | number)[]
  code: 'unsupported_entity_type'
  entityType: string
  supported: string[]
}
export type UnsupportedEntityTypeRead = {
  loc?: (string | number)[]
  code: 'unsupported_entity_type'
  entityType: string
  supported: string[]
  message: string
}
export type InvalidDateRange = {
  loc?: (string | number)[]
  code: 'invalid_date_range'
  detail: string
}
export type InvalidDateRangeRead = {
  loc?: (string | number)[]
  code: 'invalid_date_range'
  detail: string
  message: string
}
export type FetchDataNoEntity = {
  loc?: (string | number)[]
  code: 'fetch_data_no_entity'
}
export type FetchDataNoEntityRead = {
  loc?: (string | number)[]
  code: 'fetch_data_no_entity'
  message: string
}
export type TooManyEmailAddresses = {
  loc?: (string | number)[]
  code: 'too_many_email_addresses'
  limit: number
}
export type TooManyEmailAddressesRead = {
  loc?: (string | number)[]
  code: 'too_many_email_addresses'
  limit: number
  message: string
}
export type EmailNotDeliverable = {
  loc?: (string | number)[]
  code: 'email_not_deliverable'
  detail: string
}
export type EmailNotDeliverableRead = {
  loc?: (string | number)[]
  code: 'email_not_deliverable'
  detail: string
  message: string
}
export type SchemaViolation = {
  loc?: (string | number)[]
  code: 'schema_violation'
  pydanticType: string
  detail: string
}
export type SchemaViolationRead = {
  loc?: (string | number)[]
  code: 'schema_violation'
  pydanticType: string
  detail: string
  message: string
}
export type ValidateDefinitionResponse = {
  valid: boolean
  issues?: (
    | TriggerCount
    | UnknownAction
    | UnknownParameter
    | TemplatingNotSupported
    | UnknownEventAttribute
    | OperatorNotValidForType
    | OneOfRequiresList
    | BetweenRequiresPair
    | ConditionElementsWrongType
    | ConditionValueWrongType
    | ConditionReferenceTypeMismatch
    | ConditionReferenceNotValidForOperator
    | ConditionStepNotAvailable
    | TemplateMalformed
    | TemplateEventShape
    | TemplateUnknownRoot
    | TemplateUnknownStep
    | TemplateStepNotPredecessor
    | TemplateStepShape
    | TemplateTargetNotAction
    | TemplateUnknownResultField
    | ContextPathInvalid
    | ContextUnknownEntity
    | ContextEntityUnreachable
    | ContextEntityExcludedByTrigger
    | ContextUnknownField
    | ContextNotAvailableInTrigger
    | LinkMalformed
    | LinkEntityUnknown
    | LinkNotAllowedHere
    | LinkLabelEmpty
    | RootStepNotInSteps
    | StepKeyMismatch
    | UnknownSuccessor
    | StepCycle
    | UnreachableSteps
    | BranchDefaultPosition
    | OperatorMustNotCarryValue
    | OperatorRequiresValue
    | FlatSpecWithGroups
    | GroupedSpecWithFlatKeys
    | WindowExceedsCap
    | NotHeardFromWindowTooShort
    | UnsupportedEntityType
    | InvalidDateRange
    | FetchDataNoEntity
    | TooManyEmailAddresses
    | EmailNotDeliverable
    | SchemaViolation
  )[]
}
export type ValidateDefinitionResponseRead = {
  valid: boolean
  issues?: (
    | TriggerCountRead
    | UnknownActionRead
    | UnknownParameterRead
    | TemplatingNotSupportedRead
    | UnknownEventAttributeRead
    | OperatorNotValidForTypeRead
    | OneOfRequiresListRead
    | BetweenRequiresPairRead
    | ConditionElementsWrongTypeRead
    | ConditionValueWrongTypeRead
    | ConditionReferenceTypeMismatchRead
    | ConditionReferenceNotValidForOperatorRead
    | ConditionStepNotAvailableRead
    | TemplateMalformedRead
    | TemplateEventShapeRead
    | TemplateUnknownRootRead
    | TemplateUnknownStepRead
    | TemplateStepNotPredecessorRead
    | TemplateStepShapeRead
    | TemplateTargetNotActionRead
    | TemplateUnknownResultFieldRead
    | ContextPathInvalidRead
    | ContextUnknownEntityRead
    | ContextEntityUnreachableRead
    | ContextEntityExcludedByTriggerRead
    | ContextUnknownFieldRead
    | ContextNotAvailableInTriggerRead
    | LinkMalformedRead
    | LinkEntityUnknownRead
    | LinkNotAllowedHereRead
    | LinkLabelEmptyRead
    | RootStepNotInStepsRead
    | StepKeyMismatchRead
    | UnknownSuccessorRead
    | StepCycleRead
    | UnreachableStepsRead
    | BranchDefaultPositionRead
    | OperatorMustNotCarryValueRead
    | OperatorRequiresValueRead
    | FlatSpecWithGroupsRead
    | GroupedSpecWithFlatKeysRead
    | WindowExceedsCapRead
    | NotHeardFromWindowTooShortRead
    | UnsupportedEntityTypeRead
    | InvalidDateRangeRead
    | FetchDataNoEntityRead
    | TooManyEmailAddressesRead
    | EmailNotDeliverableRead
    | SchemaViolationRead
  )[]
}
export type ValidationError = {
  loc: (string | number)[]
  msg: string
  type: string
  ctx?: object
}
export type HttpValidationError = {
  detail?: ValidationError[]
}
export type ValidateDefinitionRequest = {
  definition: {
    [key: string]: unknown
  }
}
export type Pagination = {
  /** Whether this is the first page */
  first: boolean
  /** Whether this is the last page */
  last: boolean
  /** Number of items per page */
  pageSize: number
  /** Current page number (0-indexed) */
  page: number
  /** Total number of pages */
  totalPages: number
  /** Total number of items */
  totalItems: number
}
export type PaginationMeta = {
  pagination: Pagination
}
export type ActorType = 'USER' | 'ORGANIZATION'
export type EventType =
  | 'charger.status_notification'
  | 'charger.stop_transaction'
  | 'charger.start_transaction'
  | 'charger.boot_notification'
  | 'charger.data_transfer'
  | 'charger.transaction_summary'
  | 'vehicle.telemetry'
  | 'vehicle.fault'
  | 'site.power_utilization'
  | 'der.fault'
  | 'entity.connectivity'
  | 'condition.opened'
  | 'condition.resolved'
  | 'scheduled'
export type BooleanLogic = 'AND' | 'OR'
export type Operator =
  | 'EQUALS'
  | 'NOT_EQUALS'
  | 'GREATER_THAN'
  | 'GREATER_THAN_OR_EQUAL'
  | 'LESS_THAN'
  | 'LESS_THAN_OR_EQUAL'
  | 'CONTAINS'
  | 'STARTS_WITH'
  | 'ENDS_WITH'
  | 'ONE_OF'
  | 'IS_TRUE'
  | 'IS_FALSE'
  | 'BETWEEN'
export type Condition = {
  field: string
  operator: Operator
  value?: unknown | null
}
export type ConditionGroup = {
  logic?: BooleanLogic
  conditions: Condition[]
}
export type GroupedConditionSpec = {
  groups: ConditionGroup[]
}
export type FlatConditionSpec = {
  field: string
  operator: Operator
  value?: unknown | null
}
export type ResolutionPolicy = 'on_contrary_reading'
export type ImmediateTemporalTrigger = {
  type: 'immediate'
  resolution?: ResolutionPolicy
}
export type WindowUnit = 'seconds' | 'minutes' | 'hours' | 'days'
export type FillPolicy = 'none' | 'hold_last'
export type SustainedTemporalTrigger = {
  /** Window magnitude, e.g. 5. */
  windowValue: number
  /** Window granularity (no default — author chooses). */
  windowUnit: WindowUnit
  type: 'sustained'
  fill?: FillPolicy
  requireFullWindow?: boolean
}
export type CountOperator = 'GREATER_THAN_OR_EQUAL' | 'GREATER_THAN' | 'EQUALS'
export type AggregateTemporalTrigger = {
  /** Window magnitude, e.g. 5. */
  windowValue: number
  /** Window granularity (no default — author chooses). */
  windowUnit: WindowUnit
  type: 'aggregate'
  /** Occurrence count the in-window count is compared to. */
  threshold: number
  /** How the in-window count is compared to the threshold (no default). */
  countOperator: CountOperator
}
export type EventTrigger = {
  kind: 'event'
  eventType: EventType
  scope?: GroupedConditionSpec | FlatConditionSpec | null
  condition?: GroupedConditionSpec | FlatConditionSpec | null
  correlationKeyField?: string | null
  temporal?: (ImmediateTemporalTrigger | SustainedTemporalTrigger | AggregateTemporalTrigger) | null
}
export type ScheduleSpecOutput = {
  /** IANA timezone the schedule's wall-clock times are interpreted in. */
  timezone: string
  /** Naive local wall-clock anchor the recurrence recurs from. */
  dtstart: string
  /** An open-ended iCal RRULE, e.g. 'FREQ=WEEKLY;BYDAY=MO,TH'. */
  rrule: string
}
export type ScheduledTriggerOutput = {
  kind: 'scheduled'
  schedule: ScheduleSpecOutput
}
export type NotHeardFromTrigger = {
  kind: 'not_heard_from'
  /** The kind of entity to watch, e.g. 'charger'. Validated against the kinds the connectivity feed covers. */
  entityType: string
  /** Window magnitude, e.g. 15. */
  windowValue: number
  /** Window granularity (no default — author chooses). */
  windowUnit: WindowUnit
  /** Which entities of that kind to watch, e.g. the chargers at one site. Omit to watch every entity of the kind the workflow's organization owns. */
  scope?: GroupedConditionSpec | FlatConditionSpec | null
}
export type UserRecipient = {
  type: 'user'
  userId: string
}
export type UserGroupRecipient = {
  type: 'group'
  groupId: string
}
export type EmailRecipient = {
  type: 'email'
  emailAddress: string
}
export type SendEmailParametersAuthoring = {
  recipients: (UserRecipient | UserGroupRecipient | EmailRecipient)[]
  subject: string
  body: string
}
export type EmailUsersStep = {
  id: string
  type: 'ACTION'
  action: 'email.users'
  parameters: SendEmailParametersAuthoring
  nextStepIds?: string[]
}
export type SendSmsParametersAuthoring = {
  recipients: (UserRecipient | UserGroupRecipient)[]
  body: string
}
export type SmsSendStep = {
  id: string
  type: 'ACTION'
  action: 'sms.send'
  parameters: SendSmsParametersAuthoring
  nextStepIds?: string[]
}
export type ChargerResetParametersAuthoring = {
  chargerId: string
  resetType?: 'soft' | 'hard'
}
export type ChargerResetStep = {
  id: string
  type: 'ACTION'
  action: 'charger.reset'
  parameters: ChargerResetParametersAuthoring
  nextStepIds?: string[]
}
export type ChargerRemoteStartParametersAuthoring = {
  chargerId: string
  tagId: string
  connectorId: number | string
}
export type ChargerRemoteStartStep = {
  id: string
  type: 'ACTION'
  action: 'charger.remote_start'
  parameters: ChargerRemoteStartParametersAuthoring
  nextStepIds?: string[]
}
export type DurationUnit = 'SECONDS' | 'MINUTES' | 'HOURS' | 'DAYS'
export type WaitParametersAuthoring = {
  durationValue: number
  durationUnit: DurationUnit
}
export type WaitStep = {
  id: string
  type: 'ACTION'
  action: 'wait'
  parameters: WaitParametersAuthoring
  nextStepIds?: string[]
}
export type AbsoluteDateRange = {
  type: 'absolute'
  start: string
  end: string
}
export type RelativeUnit = 'hours' | 'days' | 'weeks' | 'months'
export type RelativeOffset = {
  amount: number
  unit: RelativeUnit
}
export type RelativeDateRangeAuthoring = {
  type: 'relative'
  anchor?: string | null
  start: RelativeOffset
  end?: RelativeOffset | null
}
export type ReportFormat = 'csv' | 'xlsx'
export type TripsReportAuthoring = {
  dateRange: AbsoluteDateRange | RelativeDateRangeAuthoring
  format: ReportFormat
  recipients: (UserRecipient | UserGroupRecipient | EmailRecipient)[]
  reportType: 'trips'
  organizationId?: string | null
  fleetId?: string | null
  siteIds?: string[] | null
}
export type VehicleActivityReportAuthoring = {
  dateRange: AbsoluteDateRange | RelativeDateRangeAuthoring
  format: ReportFormat
  recipients: (UserRecipient | UserGroupRecipient | EmailRecipient)[]
  reportType: 'vehicle_activity'
  organizationId?: string | null
  fleetId?: string | null
  siteIds?: string[] | null
}
export type CommissioningState = 'commissioned' | 'decommissioned' | 'precommissioned'
export type IntervalPeriod = '15m' | '1h'
export type MeterValueIntervalReportAuthoring = {
  dateRange: AbsoluteDateRange | RelativeDateRangeAuthoring
  format: ReportFormat
  recipients: (UserRecipient | UserGroupRecipient | EmailRecipient)[]
  reportType: 'meter_value_interval'
  siteId: string
  nodeIds?: string[] | null
  commissioningStates?: CommissioningState[] | null
  intervalPeriod?: IntervalPeriod
}
export type DetailLevel = 'transaction' | 'rate'
export type ChargingTransactionsReportAuthoring = {
  dateRange: AbsoluteDateRange | RelativeDateRangeAuthoring
  format: ReportFormat
  recipients: (UserRecipient | UserGroupRecipient | EmailRecipient)[]
  reportType: 'charging_transactions'
  organizationId?: string | null
  siteIds?: string[] | null
  nodeIds?: string[] | null
  commissioningStates?: CommissioningState[] | null
  chargerId?: string | null
  detailLevel?: DetailLevel
}
export type RunReportParametersAuthoring =
  | TripsReportAuthoring
  | VehicleActivityReportAuthoring
  | MeterValueIntervalReportAuthoring
  | ChargingTransactionsReportAuthoring
export type ReportRunStep = {
  id: string
  type: 'ACTION'
  action: 'report.run'
  parameters: RunReportParametersAuthoring
  nextStepIds?: string[]
}
export type NotificationPriority = 'Low' | 'Medium' | 'High' | 'Critical'
export type NotificationCreateParametersAuthoring = {
  recipients: (UserRecipient | UserGroupRecipient)[]
  title: string
  body: string
  priority: NotificationPriority
}
export type NotificationCreateStep = {
  id: string
  type: 'ACTION'
  action: 'notification.create'
  parameters: NotificationCreateParametersAuthoring
  nextStepIds?: string[]
}
export type FetchDataParametersAuthoring = {
  chargerId?: string | null
  vehicleId?: string | null
  siteId?: string | null
}
export type FetchDataStep = {
  id: string
  type: 'ACTION'
  action: 'fetch.data'
  parameters: FetchDataParametersAuthoring
  nextStepIds?: string[]
}
export type ActionStepDefinition =
  | EmailUsersStep
  | SmsSendStep
  | ChargerResetStep
  | ChargerRemoteStartStep
  | WaitStep
  | ReportRunStep
  | NotificationCreateStep
  | FetchDataStep
export type Branch = {
  condition?: GroupedConditionSpec | FlatConditionSpec | null
  stepIds?: string[]
}
export type ConditionalStepDefinition = {
  id: string
  type: 'CONDITIONAL'
  branches: Branch[]
}
export type StepDefinition = ActionStepDefinition | ConditionalStepDefinition
export type WorkflowDefinitionOutput = {
  triggers?: (EventTrigger | ScheduledTriggerOutput | NotHeardFromTrigger)[]
  rootStepId: string
  steps: {
    [key: string]: StepDefinition
  }
}
export type WorkflowResponse = {
  id: string
  orgId: string
  name: string
  description?: string
  enabled: boolean
  dryRun: boolean
  activeVersionId?: string
  archivedAt?: string
  createdAt: string
  updatedAt: string
  createdById: string
  createdByType: ActorType
  definition?: WorkflowDefinitionOutput | null
  draftDefinition?: {
    [key: string]: unknown
  }
}
export type PagedResponseWorkflowResponse = {
  meta: PaginationMeta
  data: WorkflowResponse[]
}
export type CreateWorkflowRequest = {
  name: string
  description?: string
  draftDefinition?: {
    [key: string]: unknown
  }
}
export type PatchWorkflowRequest = {
  name?: string
  description?: string
  draftDefinition?: {
    [key: string]: unknown
  }
  activeVersionId?: string
  enabled?: boolean
  dryRun?: boolean
}
export type WorkflowVersionSummary = {
  id: string
  workflowId: string
  versionNumber: number
  createdAt: string
  createdById: string
  createdByType: ActorType
}
export type PagedResponseWorkflowVersionSummary = {
  meta: PaginationMeta
  data: WorkflowVersionSummary[]
}
export type WorkflowVersionResponse = {
  id: string
  workflowId: string
  versionNumber: number
  createdAt: string
  createdById: string
  createdByType: ActorType
  definition: WorkflowDefinitionOutput
}
export type ScheduleSpecInput = {
  /** IANA timezone the schedule's wall-clock times are interpreted in. */
  timezone: string
  /** Naive local wall-clock anchor the recurrence recurs from. */
  dtstart: string
  /** An open-ended iCal RRULE, e.g. 'FREQ=WEEKLY;BYDAY=MO,TH'. */
  rrule: string
}
export type ScheduledTriggerInput = {
  kind: 'scheduled'
  schedule: ScheduleSpecInput
}
export type WorkflowDefinitionInput = {
  triggers?: (EventTrigger | ScheduledTriggerInput | NotHeardFromTrigger)[]
  rootStepId: string
  steps: {
    [key: string]: StepDefinition
  }
}
export type PublishVersionRequest = {
  definition?: WorkflowDefinitionInput
}
export type RunState = 'RUNNING' | 'SUCCEEDED' | 'FAILED' | 'CANCELING' | 'CANCELED'
export type RunSummary = {
  id: string
  workflowId: string
  workflowVersion: number
  state: RunState
  dryRun: boolean
  triggerId: string
  triggeringEventId: string
  parentRunId?: string
  canceledById?: string
  canceledByType?: ActorType
  canceledReason?: string
  createdAt: string
  endedAt?: string
}
export type PagedResponseRunSummary = {
  meta: PaginationMeta
  data: RunSummary[]
}
export type StepState = 'PENDING' | 'RUNNING' | 'SUSPENDED' | 'SUCCEEDED' | 'FAILED' | 'CANCELED'
export type InspectorStepStatus = 'NOT_STARTED' | 'BYPASSED'
export type SentEmailToUser = {
  type: 'user'
  user_id: string
  message_id: string
}
export type SentEmailToAddress = {
  type: 'email'
  email_address: string
  message_id: string
}
export type SendEmailResult = {
  sent: (SentEmailToUser | SentEmailToAddress)[]
  skipped: string[]
}
export type StepError = {
  type: string
  message: string
  retriable?: boolean
  details?: {
    [key: string]: unknown
  } | null
}
export type FailureResult = {
  error: StepError
}
export type EmailUsersStepView = {
  id: string
  status: StepState | InspectorStepStatus
  attempt?: number
  createdAt?: string
  startedAt?: string
  endedAt?: string
  type: 'ACTION'
  nextStepIds?: string[]
  action: 'email.users'
  result?: SendEmailResult | FailureResult
}
export type SentSms = {
  user_id: string
  message_id: string
}
export type SendSmsResult = {
  sent: SentSms[]
  skipped: string[]
}
export type SmsSendStepView = {
  id: string
  status: StepState | InspectorStepStatus
  attempt?: number
  createdAt?: string
  startedAt?: string
  endedAt?: string
  type: 'ACTION'
  nextStepIds?: string[]
  action: 'sms.send'
  result?: SendSmsResult | FailureResult
}
export type ChargerResetResult = {
  charger_id: string
  applied: boolean
  status_code: number
}
export type ChargerResetStepView = {
  id: string
  status: StepState | InspectorStepStatus
  attempt?: number
  createdAt?: string
  startedAt?: string
  endedAt?: string
  type: 'ACTION'
  nextStepIds?: string[]
  action: 'charger.reset'
  result?: ChargerResetResult | FailureResult
}
export type ChargerRemoteStartResult = {
  charger_id: string
  connector_id: number
  tag_id: string
  transaction_id: string | null
  communication_result:
    | ('Applied' | 'Invalid' | 'NotConnected' | 'NotSupported' | 'Pending' | 'Rejected' | 'Timeout' | 'Unknown')
    | null
  status_code: number
}
export type ChargerRemoteStartStepView = {
  id: string
  status: StepState | InspectorStepStatus
  attempt?: number
  createdAt?: string
  startedAt?: string
  endedAt?: string
  type: 'ACTION'
  nextStepIds?: string[]
  action: 'charger.remote_start'
  result?: ChargerRemoteStartResult | FailureResult
}
export type WaitResult = {}
export type WaitStepView = {
  id: string
  status: StepState | InspectorStepStatus
  attempt?: number
  createdAt?: string
  startedAt?: string
  endedAt?: string
  type: 'ACTION'
  nextStepIds?: string[]
  action: 'wait'
  result?: FailureResult | WaitResult
}
export type RunReportResult = {
  report_format: ReportFormat
  delivered: (SentEmailToUser | SentEmailToAddress)[]
  skipped: string[]
}
export type ReportRunStepView = {
  id: string
  status: StepState | InspectorStepStatus
  attempt?: number
  createdAt?: string
  startedAt?: string
  endedAt?: string
  type: 'ACTION'
  nextStepIds?: string[]
  action: 'report.run'
  result?: RunReportResult | FailureResult
}
export type WorkflowscorecatalogactionsCreatedNotification = {
  user_id: string
  notification_id: string
}
export type NotificationCreateResult = {
  created: WorkflowscorecatalogactionsCreatedNotification[]
  skipped: string[]
}
export type NotificationCreateStepView = {
  id: string
  status: StepState | InspectorStepStatus
  attempt?: number
  createdAt?: string
  startedAt?: string
  endedAt?: string
  type: 'ACTION'
  nextStepIds?: string[]
  action: 'notification.create'
  result?: NotificationCreateResult | FailureResult
}
export type FetchedCharger = {
  charger_id: string
  charger_name?: string | null
  site_id?: string | null
  status?: string | null
  operational_status?: string | null
  connectivity_status?: string | null
  vendor?: string | null
  model?: string | null
  firmware_version?: string | null
  max_power?: number | null
  last_heard_at?: string | null
}
export type FetchedVehicle = {
  vehicle_id: string
  vehicle_name?: string | null
  organization_id?: string | null
  state_of_charge?: number | null
  vehicle_status?: string | null
  latitude?: number | null
  longitude?: number | null
  remaining_range?: number | null
  odometer?: number | null
  home_site_id?: string | null
}
export type FetchedSite = {
  site_id: string
  site_name?: string | null
  timezone?: string | null
  power_ceiling?: number | null
  num_chargers?: number | null
  num_vehicles?: number | null
  latitude?: number | null
  longitude?: number | null
  address?: string | null
}
export type FetchDataResult = {
  charger?: FetchedCharger | null
  vehicle?: FetchedVehicle | null
  site?: FetchedSite | null
  retrieved_at: string
}
export type FetchDataStepView = {
  id: string
  status: StepState | InspectorStepStatus
  attempt?: number
  createdAt?: string
  startedAt?: string
  endedAt?: string
  type: 'ACTION'
  nextStepIds?: string[]
  action: 'fetch.data'
  result?: FetchDataResult | FailureResult
}
export type ActionStepView =
  | EmailUsersStepView
  | SmsSendStepView
  | ChargerResetStepView
  | ChargerRemoteStartStepView
  | WaitStepView
  | ReportRunStepView
  | NotificationCreateStepView
  | FetchDataStepView
export type RunStepBranchView = {
  stepIds: string[]
  taken?: boolean
}
export type ConditionalStepView = {
  id: string
  status: StepState | InspectorStepStatus
  attempt?: number
  createdAt?: string
  startedAt?: string
  endedAt?: string
  type: 'CONDITIONAL'
  branches: RunStepBranchView[]
  takenBranch?: number
}
export type RunStepView = ActionStepView | ConditionalStepView
export type RunDetailResponse = {
  id: string
  workflowId: string
  workflowVersion: number
  state: RunState
  dryRun: boolean
  triggerId: string
  triggeringEventId: string
  parentRunId?: string
  canceledById?: string
  canceledByType?: ActorType
  canceledReason?: string
  createdAt: string
  endedAt?: string
  rootStepId: string
  steps: RunStepView[]
}
export type LogLevel = 'DEBUG' | 'INFO' | 'WARNING' | 'ERROR'
export type StepLogEntry = {
  level: LogLevel
  ts: string
  seq: number
  message: string
}
export type StepLogs = {
  stepId: string
  entries: StepLogEntry[]
}
export type RunLogsResponse = {
  steps: StepLogs[]
}
export type CancelOutcome = 'CANCELING' | 'CANCELED' | 'ALREADY_REQUESTED' | 'ALREADY_FINISHED' | 'NOT_FOUND'
export type CancelResponse = {
  outcome: CancelOutcome
}
export type RerunResponse = {
  runId: string
}
export type ApischemasinappnotificationCreatedNotification = {
  userId: string
  id: string
}
export type CreatedInAppNotifications = {
  created: ApischemasinappnotificationCreatedNotification[]
}
export type NotificationTextSegment = {
  type: 'text'
  text: string
}
export type NotificationLinkSegment = {
  type: 'link'
  label: string
  entity: LinkEntityKind
  entityId: string
}
export type CreateInAppNotificationsRequest = {
  orgId: string
  userIds: string[]
  title: string
  body: string
  bodySegments?: (NotificationTextSegment | NotificationLinkSegment)[]
  priority: NotificationPriority
  source?: string
  workflowRunId?: string
  stepDefinitionId?: string
  dedupeKey: string
}
export type InAppNotificationResponse = {
  id: string
  orgId: string
  userId: string
  title: string
  body: string
  bodySegments?: (NotificationTextSegment | NotificationLinkSegment)[]
  priority: NotificationPriority
  read: boolean
  source: string
  workflowRunId?: string
  stepDefinitionId?: string
  workflowId?: string
  workflowName?: string
  createdAt: string
}
export type PagedResponseInAppNotificationResponse = {
  meta: PaginationMeta
  data: InAppNotificationResponse[]
}
export type MarkAllReadResult = {
  updated: number
}
export type MarkReadByIdsResult = {
  updated: number
}
export type MarkReadByIdsRequest = {
  notificationIds: string[]
}
export type MarkReadRequest = {
  read: boolean
}
export const {
  useValidateWorkflowDefinitionMutation,
  useListWorkflowsQuery,
  useLazyListWorkflowsQuery,
  useCreateWorkflowMutation,
  useGetWorkflowQuery,
  useLazyGetWorkflowQuery,
  usePatchWorkflowMutation,
  useArchiveWorkflowMutation,
  useListVersionsQuery,
  useLazyListVersionsQuery,
  usePublishVersionMutation,
  useGetVersionQuery,
  useLazyGetVersionQuery,
  useListRunsQuery,
  useLazyListRunsQuery,
  useGetRunQuery,
  useLazyGetRunQuery,
  useGetRunLogsQuery,
  useLazyGetRunLogsQuery,
  useCancelRunMutation,
  useRerunRunMutation,
  useCreateInAppNotificationsMutation,
  useListInAppNotificationsQuery,
  useLazyListInAppNotificationsQuery,
  useMarkAllInAppNotificationsReadMutation,
  useMarkInAppNotificationsReadByIdsMutation,
  useMarkInAppNotificationReadMutation
} = injectedRtkApi
